import { ID } from "../../@types/main"
import { queryClient } from "./query-client"
import { client } from "@/hooks/useClient"
import { proxy } from "valtio"
import { Entity } from "./schemas"
import { PaginatedResponse } from "@/types"

export interface StaticStore<T extends Entity, A = {}> {
  list: T[]
  count: number
  fetched: boolean
  fetching: boolean
  fetchedError: boolean
  actions: {
    // Remote
    fetch: () => Promise<PaginatedResponse<T>>
    delete: (id: ID) => Promise<{ success: boolean }>
    update: (id: ID, payload: Partial<T>) => Promise<T>
    create: (payload: Partial<T>) => Promise<T>

    // Local
    removeLocal: (id: ID) => void
    updateLocal: (data: Partial<T>) => T
    addLocal: (data: T) => T
  } & A
}

export const staticStoreInitializer = {
  loadaded: false,
}

export const createStaticStore = <T extends Entity, A = {}, S = {}>(
  initialState: {
    endpoint: string
  } & S,
  actions: A = {} as any,
): StaticStore<T, A> & S => {
  const state = proxy<StaticStore<T, any> & S>({
    count: 0,
    fetched: false,
    fetching: false,
    fetchedError: false,
    list: [],
    ...initialState,
    actions: {
      fetch: () => {
        state.fetching = true
        return queryClient
          .fetchQuery({
            queryKey: [initialState.endpoint],
            queryFn: () => client.get<PaginatedResponse<T>>(initialState.endpoint),
            staleTime: Infinity,
          })
          .then((res) => {
            state.list = res.data.data as any
            state.count = res.data.count
            state.fetched = true
            state.fetchedError = false

            return res.data
          })
          .catch((err) => {
            state.fetchedError = true
            throw err
          })
          .finally(() => {
            state.fetching = false
          })
      },
      create: (payload: Partial<T>) => {
        return client.post<T>(initialState.endpoint, payload).then((res) => {
          state.actions.addLocal(res.data)
          return res.data
        })
      },
      delete: (id) => {
        return client.delete(`${initialState.endpoint}/${id}`).then(() => {
          state.actions.removeLocal(id)
          return { success: true }
        })
      },
      removeLocal: (id) => {
        const index = state.list.findIndex((item) => item.id === id)
        if (index !== -1) {
          state.list.splice(index, 1)
        }
      },
      update: (id, payload) => {
        return client.patch<T>(`${initialState.endpoint}/${id}`, payload).then((res) => {
          state.actions.updateLocal(res.data)
          return res.data
        })
      },
      updateLocal: (data) => {
        const index = state.list.findIndex((item) => item.id === data.id)
        if (index !== -1) {
          state.list[index] = { ...state.list[index], ...data }
        }
        return state.list[index]
      },
      addLocal: (data) => {
        state.list.unshift(data)
        return data
      },
      ...actions,
    },
  })

  return state
}
